import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { Button, Descriptions, Spin, Table, Tag, Space, Select, Input } from "antd";
import { MobileOutlined } from "@ant-design/icons";
import moment from "moment";
import { getCampaignSentBatchDetail } from "../../../api/Services";
import styles from "./SentCampaignBatchDetail.module.scss";
import { Doughnut } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";

ChartJS.register(ArcElement, Tooltip, Legend);

const SentCampaignBatchDetail: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { id, batchId } = useParams<{ id: string; batchId: string }>();

  const getBasePath = () => {
    if (location.pathname.startsWith("/commercial-intelligence")) {
      return "/commercial-intelligence/campaigns";
    }
    return "/promotions/campaigns";
  };

  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState<any>(null);
  const [recipients, setRecipients] = useState<any[]>([]);
  const [totalSize, setTotalSize] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);
  const [phoneFilter, setPhoneFilter] = useState("");
  const [clientFilter, setClientFilter] = useState("");
  const [filters, setFilters] = useState<{ status?: string; phone?: string; client?: string }>({});
  const debounceRef = useRef<number | null>(null);

  useEffect(() => {
    const run = async () => {
      if (!id || !batchId) return;
      setLoading(true);
      try {
        const data = await getCampaignSentBatchDetail(id, batchId, {
          page,
          size: pageSize,
          status: filters.status,
          phone: filters.phone,
          client: filters.client,
        });
        setDetail(data);
        setRecipients(data?.recipients || []);
        setTotalSize(data?.recipientsTotalElements || 0);
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [id, batchId, page, pageSize, filters]);

  // Auto-apply text filters with debounce (no "Filtrar" click)
  useEffect(() => {
    if (debounceRef.current) {
      window.clearTimeout(debounceRef.current);
    }
    debounceRef.current = window.setTimeout(() => {
      setPage(1);
      setFilters((prev) => ({
        ...prev,
        phone: phoneFilter.trim() || undefined,
        client: clientFilter.trim() || undefined,
      }));
    }, 450);

    return () => {
      if (debounceRef.current) {
        window.clearTimeout(debounceRef.current);
      }
    };
  }, [phoneFilter, clientFilter]);

  const statusTag = (status?: string) => {
    const s = (status || "").toLowerCase();
    if (s === "delivered") return <Tag color="green">{t("Common-MessageStatusDelivered", "Entregado")}</Tag>;
    if (s === "read") return <Tag color="purple">{t("Common-MessageStatusRead", "Leído")}</Tag>;
    if (s === "sent") return <Tag color="blue">{t("Common-MessageStatusSent", "Enviado")}</Tag>;
    if (s === "queued") return <Tag color="default">{t("Common-MessageStatusQueued", "En cola")}</Tag>;
    if (s === "failed") return <Tag color="red">{t("Common-MessageStatusFailed", "Fallido")}</Tag>;
    if (s === "undelivered") return <Tag color="red">{t("Common-MessageStatusUndelivered", "No entregado")}</Tag>;
    return <Tag>{status || "-"}</Tag>;
  };

  const columns = [
    {
      title: t("Common-Name", "Nombre"),
      dataIndex: "name",
      render: (_: any, r: any) => [r.name, r.lastName].filter(Boolean).join(" ") || "-",
    },
    {
      title: t("Common-Phone", "Teléfono"),
      dataIndex: "phone",
      render: (v: string) => v || "-",
    },
    {
      title: t("Common-Status", "Estado"),
      dataIndex: "providerMessageStatus",
      render: (v: string) => statusTag(v),
    },
    {
      title: t("Common-Error", "Error"),
      dataIndex: "providerErrorMessage",
      render: (v: string) => v || "-",
    },
  ];

  const chart = React.useMemo(() => {
    const total = Number(detail?.batchTotalRecipients || 0);
    const read = Number(detail?.readCount || 0);
    const delivered = Number(detail?.deliveredCount || 0);
    const failed = Number(detail?.failedCount || 0);
    const pending = Number(detail?.pendingCount || Math.max(0, total - read - delivered - failed));

    return {
      data: {
        labels: [
          `${t("Common-MessageStatusRead", "Leído")}s (${read})`,
          `${t("Common-MessageStatusDelivered", "Entregado")}s (${delivered})`,
          `Pendientes (${pending})`,
          `${t("Common-MessageStatusFailed", "Fallido")}s (${failed})`,
        ],
        datasets: [
          {
            data: [read, delivered, pending, failed],
            backgroundColor: ["#722ed1", "#52c41a", "#fa8c16", "#ff4d4f"],
            borderWidth: 0,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        cutout: "70%",
        plugins: {
          legend: {
            position: "right" as const,
            labels: {
              usePointStyle: true,
              padding: 20,
              font: { size: 12 },
            },
          },
          tooltip: {
            callbacks: {
              label: (ctx: any) => {
                const label = String(ctx.label || "").replace(/\s*\(\d+\)\s*$/, "");
                return `${label}: ${ctx.raw}`;
              },
            },
          },
        },
      },
    };
  }, [detail, t]);

  const clearFilters = () => {
    setStatusFilter(undefined);
    setPhoneFilter("");
    setClientFilter("");
    setPage(1);
    setFilters({});
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <h1 style={{ margin: 0 }}>{t("Common-CampaignSentsTitle", "Envíos de campaña")}</h1>
        <Button onClick={() => navigate(`${getBasePath()}/${id}/sent`)}>
          {t("Common-Back", "Volver")}
        </Button>
      </div>

      <Spin spinning={loading}>
        {detail && (
          <div className={styles.grid}>
            <div>
              <div className={styles.infoAndChartRow}>
                <div className={styles.infoTableWrap}>
                  <Descriptions bordered size="small" column={1}>
                    <Descriptions.Item label={t("Common-Campaign", "Campaña")}>
                      {detail.campaignName || "-"}
                    </Descriptions.Item>
                    <Descriptions.Item label={t("Common-Template", "Plantilla")}>
                      {detail.messageTemplateName || "-"}
                    </Descriptions.Item>
                <Descriptions.Item label={t("Common-Sent", "Enviada")}>
                  {detail.firstSentAt
                    ? moment(detail.firstSentAt).format("DD-MM-YYYY HH:mm")
                    : "-"}
                </Descriptions.Item>
                <Descriptions.Item label={t("Common-TotalRecipients", "Total de destinatarios")}>
                  {detail.batchTotalRecipients ?? "-"}
                </Descriptions.Item>
              </Descriptions>
                </div>
                <div className={styles.chartWrap}>
                  <Doughnut data={chart.data as any} options={chart.options as any} />
                </div>
              </div>

              <div className={styles.filtersRow}>
                <Select
                  allowClear
                  placeholder={t("Common-Status", "Estado")}
                  value={statusFilter}
                  onChange={(v) => {
                    setStatusFilter(v);
                    setPage(1);
                    setFilters((prev) => ({ ...prev, status: v || undefined }));
                  }}
                  options={[
                    { value: "read", label: t("Common-MessageStatusRead", "Leído") },
                    { value: "delivered", label: t("Common-MessageStatusDelivered", "Entregado") },
                    { value: "sent", label: t("Common-MessageStatusSent", "Enviado") },
                    { value: "queued", label: t("Common-MessageStatusQueued", "En cola") },
                    { value: "failed", label: t("Common-MessageStatusFailed", "Fallido") },
                    { value: "undelivered", label: t("Common-MessageStatusUndelivered", "No entregado") },
                  ]}
                />
                <Input
                  placeholder={t("Common-Phone", "Teléfono")}
                  value={phoneFilter}
                  onChange={(e) => setPhoneFilter(e.target.value)}
                />
                <Input
                  placeholder={t("Common-Name", "Cliente")}
                  value={clientFilter}
                  onChange={(e) => setClientFilter(e.target.value)}
                />
                <div className={styles.clearButtonWrap}>
                  <Button onClick={clearFilters}>{t("Common-Clear", "Limpiar")}</Button>
                </div>
              </div>

              <div style={{ marginTop: 12 }}>
                <Table
                  size="small"
                  rowKey={(r: any) => `${r.clientId}-${r.providerMessageSid || ""}`}
                  dataSource={recipients}
                  columns={columns as any}
                  pagination={{
                    current: page,
                    pageSize,
                    total: totalSize,
                    showSizeChanger: true,
                    onChange: (p, ps) => {
                      setPage(p);
                      setPageSize(ps || 10);
                    },
                  }}
                />
              </div>
            </div>

            <div>
              <div className={styles.previewPhone}>
                <div className={styles.previewScreen}>
                  <div className={styles.whatsappHeader}>
                    <MobileOutlined className={styles.whatsappHeaderIcon} />
                    <span>{t("Common-YourPhoneNumber", "Tu número de teléfono")}</span>
                  </div>
                  <div className={styles.chatArea}>
                    <div className={styles.bubble}>
                      {detail.messageTemplateBody || t("Common-PreviewEmpty", "La vista previa aparecerá aquí.")}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </Spin>
    </div>
  );
};

export default SentCampaignBatchDetail;

